import { loadStripe } from '@stripe/stripe-js/pure'
import type {
  Stripe,
  StripeCheckoutElementsSdk,
  StripeCheckoutLoadActionsSuccess,
  StripeCheckoutSession,
} from '@stripe/stripe-js'

export interface LocalizedCheckout {
  sessionId: string
  sdk: StripeCheckoutElementsSdk
  actions: StripeCheckoutLoadActionsSuccess
}

export interface LocalizedPrice {
  currency: string
  divisor: number
  dueToday: number
  recurring: number | null
}

let stripeKey = ''
let stripePromise: Promise<Stripe | null> | undefined

function stripeFor(publishableKey: string) {
  if (!stripePromise || stripeKey !== publishableKey) {
    stripeKey = publishableKey
    stripePromise = loadStripe(publishableKey)
  }
  return stripePromise
}

export async function openLocalizedCheckout(
  publishableKey: string, clientSecret: string, sessionId: string,
): Promise<LocalizedCheckout> {
  const stripe = await stripeFor(publishableKey)
  if (!stripe) throw new Error('Stripe.js is unavailable')
  const sdk = stripe.initCheckoutElementsSdk({
    clientSecret,
    adaptivePricing: { allowed: true },
    elementsOptions: {
      appearance: {
        theme: 'night',
        variables: { colorPrimary: '#d7ff00', colorBackground: '#1c1c1e', borderRadius: '12px' },
      },
    },
  })
  const loaded = await sdk.loadActions()
  if (loaded.type === 'error') throw new Error(loaded.error.message)
  return { sessionId, sdk, actions: loaded.actions }
}

export function localizedPrice(session: StripeCheckoutSession): LocalizedPrice {
  return {
    currency: session.currency,
    divisor: session.minorUnitsAmountDivisor || 100,
    dueToday: session.total.total.minorUnitsAmount,
    recurring: session.recurring?.dueNext.total.minorUnitsAmount ?? null,
  }
}

export function formatLocalized(price: LocalizedPrice, minorUnits: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currency.toUpperCase(),
  }).format(minorUnits / price.divisor)
}
